import React, { useState, useEffect } from "react";
import { Button, styled, Box, Typography } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { GET_GA_PROPERTIES } from "../../gql/queries/ga4";
import GADashboard from "./GADashboard";

const GAPropertySelectorWrapper = styled("section")(({ theme }) => ({
  marginTop: "20px",

  "& .propertyCard": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#fff",
    padding: "24px",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    // marginBottom: "20px",
  },

  "& h2": {
    fontSize: "20px",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: "16px",
  },

  "& h3": {
    fontSize: "15px",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: "14px",
  },

  "& .helpText": {
    fontSize: "13px",
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    marginBottom: "18px",
  },

  /* Loading */
  "& .loadingState": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "30px",
    color: theme.palette.text.secondary,
  },

  "& .spinner": {
    width: "40px",
    height: "40px",
    border: "3px solid rgba(0,0,0,0.1)",
    borderTop: `3px solid ${theme.palette.primary.main}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "15px",
  },

  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },

  /* Info / Error */
  "& .infoState": {
    padding: "14px",
    backgroundColor: theme.palette.mode === "dark" ? "#102a43" : "#e3f2fd",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: "6px",
    marginBottom: "18px",
    color: theme.palette.primary.main,
    fontSize: "13px",
  },

  /* Properties list */
  "& .propertiesList": {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxHeight: "400px",
    overflowY: "auto",
    marginBottom: "16px",
  },

  "& .propertyItem": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px",
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8f9fa",
    borderRadius: "8px",
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "all 0.2s",

    "&:hover": {
      borderColor: theme.palette.primary.main,
    },

    "&.selected": {
      backgroundColor: theme.palette.mode === "dark" ? "#2a2a2a" : "#fff8e1",
      borderColor: theme.palette.primary.main,
    },
  },

  "& .propertyInfo": {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  "& .propertyName": {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: "14px",
  },

  "& .propertyId": {
    fontSize: "12px",
    color: theme.palette.text.secondary,
    fontFamily: "monospace",
  },

  "& .accountName": {
    fontSize: "11px",
    color: theme.palette.text.secondary,
  },

  "& .propertyMeta": {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "4px",
    fontSize: "11px",
    color: theme.palette.text.secondary,
  },

  "& .propertyType": {
    backgroundColor: theme.palette.mode === "dark" ? "#1e3a5f" : "#e3f2fd",
    color: theme.palette.primary.main,
    padding: "2px 8px",
    borderRadius: "4px",
    fontSize: "11px",
  },

  "& .actions": {
    marginTop: "14px",
    paddingTop: "14px",
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  "& .manualToggle": {
    marginTop: "14px",
    textAlign: "center",
  },

  "& .linkBtn": {
    background: "none",
    border: "none",
    color: theme.palette.primary.main,
    fontSize: "13px",
    cursor: "pointer",
    textDecoration: "underline",
  },

  "& .loadbtn": {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.text.primary,
    "&:hover": {
      borderColor: theme.palette.primary.dark,
      backgroundColor: theme.palette.primary.dark,
    },
  },

  "& .manualInput": {
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  "& .propertyForm": {
    display: "flex",
    gap: "14px",
    alignItems: "flex-end",

    "@media (max-width:768px)": {
      flexDirection: "column",
    },
  },

  "& input": {
    width: "100%",
    padding: "12px",
    borderRadius: "6px",
    border: `1px solid ${theme.palette.divider}`,
    fontFamily: "monospace",
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },

  "& .refreshSection": {
    marginTop: "20px",
    textAlign: "right",
  },
}));

function GAPropertySelector({ email, authStatus }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [manualPropertyId, setManualPropertyId] = useState("");
  const [useManualInput, setUseManualInput] = useState(false);
  const [fetchGaProperties] = useLazyQuery(GET_GA_PROPERTIES, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (authStatus?.authenticated) {
      fetchProperties();
    }
  }, [authStatus]);

  // const fetchProperties = async () => {
  //   setLoading(true);
  //   setError(null);

  //   try {
  //     const response = await fetch(`http://localhost:8000/api/ga/properties?email=${encodeURIComponent(email || "")}`);
  //     const data = await response.json();

  //     if (data.success && data.properties) {
  //       setProperties(data.properties);
  //       if (data.properties.length === 0) {
  //         setUseManualInput(true);
  //       }
  //     } else {
  //       // Check if it's an API not enabled error
  //       const errorDetail = data.detail || "";
  //       if (errorDetail.includes("SERVICE_DISABLED") || errorDetail.includes("Admin API")) {
  //         setError("Google Analytics Admin API is not enabled. Please enter your Property ID manually below.");
  //       } else {
  //         setError("Could not load properties automatically. Please enter your Property ID manually.");
  //       }
  //       setUseManualInput(true);
  //     }
  //   } catch (err) {
  //     console.error("Error fetching GA properties:", err);
  //     setError("Could not load properties. Please enter your Property ID manually.");
  //     setUseManualInput(true);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchProperties = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await fetchGaProperties({
        variables: {
          email: email || null,
        },
      });

      const response = data?.gaProperties;

      if (response?.success && Array.isArray(response?.properties)) {
        setProperties(response.properties);

        if (response.properties.length === 0) {
          setUseManualInput(true);
        }
      } else {
        setError("Could not load properties automatically. Please enter your Property ID manually.");
        setUseManualInput(true);
      }
    } catch (err) {
      console.error("Error fetching GA properties:", err);

      const message =
        err?.message?.includes("SERVICE_DISABLED") || err?.message?.includes("Admin API")
          ? "Google Analytics Admin API is not enabled. Please enter your Property ID manually below."
          : "Could not load properties. Please enter your Property ID manually.";

      setError(message);
      setUseManualInput(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySelect = property => {
    setSelectedProperty(property);
    setShowDashboard(false);
  };

  const handleLoadAnalytics = () => {
    if (selectedProperty || manualPropertyId.trim()) {
      setShowDashboard(true);
    }
  };

  const handleManualSubmit = e => {
    e.preventDefault();
    if (manualPropertyId.trim()) {
      setSelectedProperty({
        propertyId: manualPropertyId.replace("properties/", ""),
        displayName: `Property ${manualPropertyId.replace("properties/", "")}`,
      });
      setShowDashboard(true);
    }
  };

  const getPropertyId = () => {
    if (selectedProperty) {
      return selectedProperty.propertyId;
    }
    return manualPropertyId.replace("properties/", "");
  };

  if (!authStatus?.authenticated) {
    return null;
  }

  return (
    // <Box className="ga-property-selector">
    <GAPropertySelectorWrapper>
      <Box className="propertyCard">
        <Typography variant="h2">Google Analytics Properties</Typography>

        {loading ? (
          <Box className="loadingState">
            <Box className="spinner"></Box>
            <Typography component="p">Loading properties...</Typography>
          </Box>
        ) : error ? (
          <Box className="infoState">
            <Typography component="p" className="infoState">
              ℹ️ {error}
            </Typography>
          </Box>
        ) : null}

        {/* Property List */}
        {!loading && properties.length > 0 && (
          <Box className="properties-section">
            <Typography variant="h3">Select a Property</Typography>
            <Box className="propertiesList">
              {properties.map(property => (
                <Box
                  key={property.propertyId}
                  className={`propertyItem ${selectedProperty?.propertyId === property.propertyId ? "selected" : ""}`}
                  onClick={() => handlePropertySelect(property)}
                >
                  <Box className="propertyInfo">
                    <Typography component="span" className="propertyName">
                      {property.displayName}
                    </Typography>
                    <Typography component="span" className="propertyId">
                      ID: {property.propertyId}
                    </Typography>
                    {property.accountName && (
                      <Typography component="span" className="accountName">
                        Account: {property.accountName}
                      </Typography>
                    )}
                  </Box>
                  <Box className="propertyMeta">
                    {property.timeZone && <Typography component="span">{property.timeZone}</Typography>}
                    {property.propertyType && property.propertyType !== "UNKNOWN" && (
                      <Typography component="span" className="propertyType">
                        {property.propertyType.replace("PROPERTY_TYPE_", "")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>

            {selectedProperty && (
              <Box className="actions">
                <Button className="contained" onClick={handleLoadAnalytics}>
                  Load Analytics for {selectedProperty.displayName}
                </Button>
              </Box>
            )}

            <Box className="manualToggle">
              <Button className="linkBtn" onClick={() => setUseManualInput(!useManualInput)}>
                {useManualInput ? "Hide manual input" : "Or enter Property ID manually"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Manual Input */}
        {(useManualInput || properties.length === 0) && !loading && (
          <Box className="manualInput">
            <Typography variant="h3">{properties.length > 0 ? "Manual Property ID" : "Enter Property ID"}</Typography>
            <Typography component="p" className="helpText">
              Enter your GA4 Property ID. You can find it in Google Analytics under Admin → Property Settings.
              <br />
              Format: Usually a number like `123456789` or `properties/123456789`
            </Typography>

            <form onSubmit={handleManualSubmit} className="propertyForm">
              <Box>
                <Typography variant="h3">Property ID</Typography>
                <input
                  type="text"
                  id="property-id"
                  value={manualPropertyId}
                  onChange={e => {
                    setManualPropertyId(e.target.value);
                    setShowDashboard(false);
                    setSelectedProperty(null);
                  }}
                  placeholder="123456789"
                  className="propertyInput"
                />
              </Box>
              <Button type="submit" className="btn loadbtn" disabled={!manualPropertyId.trim()}>
                Load Analytics
              </Button>
            </form>
          </Box>
        )}

        {/* Refresh button */}
        {!loading && (
          <Box className="refreshSection">
            <Button className="btn btn-secondary btn-small" onClick={fetchProperties}>
              ↻ Refresh Properties
            </Button>
          </Box>
        )}
      </Box>

      {showDashboard && getPropertyId() && <GADashboard email={email} propertyId={getPropertyId()} />}
    </GAPropertySelectorWrapper>
    // {/* </Box> */}
  );
}

export default GAPropertySelector;
